export type PickerSuggestion<I, P> =
	| { kind: "issue"; item: I }
	| { kind: "project"; item: P }

/** An issue or project suggested for the thread, by its Productlane row id. */
export interface SuggestedLink {
	kind: "issue" | "project"
	id: string
}

/**
 * The empty-search list: the thread's own suggestions first, then issues and
 * projects taken in turns, issue first, so both kinds show up. When one side
 * runs out the other fills the rest.
 */
export function pickSuggestions<I, P>(
	issues: Array<I>,
	projects: Array<P>,
	limit: number,
	first: ReadonlyArray<PickerSuggestion<I, P>> = [],
): Array<PickerSuggestion<I, P>> {
	const result = first.slice(0, limit)
	const taken = new Set<unknown>(result.map((suggestion) => suggestion.item))
	const restIssues = issues.filter((issue) => !taken.has(issue))
	const restProjects = projects.filter((project) => !taken.has(project))
	let i = 0
	let p = 0
	while (
		result.length < limit &&
		(i < restIssues.length || p < restProjects.length)
	) {
		const takeIssue =
			p >= restProjects.length || (i < restIssues.length && (i + p) % 2 === 0)
		if (takeIssue) result.push({ kind: "issue", item: restIssues[i++]! })
		else result.push({ kind: "project", item: restProjects[p++]! })
	}
	return result
}

/** Moves the items with these ids to the front, in the order of `ids`. */
export function pinFirst<T>(
	items: Array<T>,
	ids: ReadonlyArray<string>,
	idOf: (item: T) => string | undefined,
): Array<T> {
	if (ids.length === 0) return items
	const byId = new Map<string, T>()
	for (const item of items) {
		const id = idOf(item)
		if (id !== undefined) byId.set(id, item)
	}
	const pinned = ids.flatMap((id) => {
		const item = byId.get(id)
		return item === undefined ? [] : [item]
	})
	if (pinned.length === 0) return items
	const set = new Set<T>(pinned)
	return [...pinned, ...items.filter((item) => !set.has(item))]
}

/**
 * The suggested links that are among the picker's rows, best first. A
 * suggestion outside the selected teams, or already linked, has no row and is
 * left out.
 */
export function resolveSuggestedLinks<I, P>(
	suggested: ReadonlyArray<SuggestedLink>,
	issues: Array<I>,
	projects: Array<P>,
	issueId: (issue: I) => string | undefined,
	projectId: (project: P) => string,
): Array<PickerSuggestion<I, P>> {
	if (suggested.length === 0) return []
	const issuesById = new Map<string, I>()
	for (const issue of issues) {
		const id = issueId(issue)
		if (id !== undefined) issuesById.set(id, issue)
	}
	const projectsById = new Map(projects.map((p) => [projectId(p), p]))
	return suggested.flatMap((link): Array<PickerSuggestion<I, P>> => {
		if (link.kind === "issue") {
			const item = issuesById.get(link.id)
			return item === undefined ? [] : [{ kind: "issue", item }]
		}
		const item = projectsById.get(link.id)
		return item === undefined ? [] : [{ kind: "project", item }]
	})
}
